import { zodResolver } from '@hookform/resolvers/zod';
import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { passwordLogin } from '@/api/auth/auth.actions';
import { PasswordLoginData } from '@/api/auth/auth.types';
import Button from '@/components/controls/Button';
import {
  defaultInputStyle,
  defaultLabelStyle,
} from '@/components/controls/CustomEntityFormField.tsx';
import CustomInput from '@/components/controls/CustomInput';
import PasswordEyeIcon from '@/components/controls/images/input/PasswordEyeIcon';
import { PasswordLoginSchema } from '@/components/zod/types';

const PasswordForm: FC = () => {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<PasswordLoginData>({
    resolver: zodResolver(PasswordLoginSchema),
  });
  const [isPassword, setIsPassword] = useState(true);
  const router = useNavigate();

  const switchIsPassword = (): void => {
    setIsPassword(!isPassword);
  };

  const onSubmit = async (data: PasswordLoginData): Promise<void> => {
    const response = await passwordLogin(data);

    if (!response.success) {
      toast.error(response.error.message);
      return;
    }

    toast.success('Successfully logged in');
    router('/');
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-col gap-5">
        <CustomInput
          name="email"
          placeholder="Enter Email Address"
          type="email"
          hasIcon={false}
          label="Email Address"
          control={control}
          error={errors.email}
          inputStyle={defaultInputStyle}
          labelStyle={defaultLabelStyle}
        />
        <CustomInput
          name="password"
          placeholder="Enter Password"
          type={isPassword ? 'password' : 'text'}
          hasIcon
          label="Password"
          icon={<PasswordEyeIcon />}
          onIconClick={switchIsPassword}
          control={control}
          error={errors.password}
          inputStyle={defaultInputStyle}
          labelStyle={defaultLabelStyle}
        />
      </div>
      <div className="flex gap-5 justify-between items-center mt-5 w-full font-medium">
        <div className="flex items-center gap-2.5 leading-[167%] text-primary-300">
          <input
            type="checkbox"
            className="
              appearance-none
              w-5 h-5
              border-2 border-honoursOrange
              rounded-md
              checked:bg-honoursOrange
              checked:border-honoursOrange
            "
          />
          <div>Remember me</div>
        </div>
        <div className="text-honoursRed cursor-pointer">
          <Link to="/forgot-password"> Forgot password </Link>
        </div>
      </div>
      <Button
        buttonStyle="justify-center items-center px-6 py-2 mt-4 text-base font-semibold text-white whitespace-nowrap bg-honoursOrange rounded-xl max-md:px-5 w-full text-lg"
        text="Login"
        buttonType={{ type: 'submit' }}
        hasImage={false}
      />
    </form>
  );
};

export default PasswordForm;
